import { useState } from 'react';
import toast from 'react-hot-toast';
import { useContactCaptureListController } from '../../controllers';
import { getErrorMessage, testContactCaptureRecord } from '../../helpers';
import { ContactCapture } from '../../types';

export type ContactCaptureConfirmation = {
  title: string;
  content: string;
  action: () => Promise<void>;
  confirmLabel: string;
};

export const useContactCaptureListActions = (
  controller: ReturnType<typeof useContactCaptureListController>,
) => {
  const [confirm, setConfirm] = useState<ContactCaptureConfirmation | null>(null);

  const runStatusChange = async (record: ContactCapture, status: 'active' | 'inactive') => {
    if (!controller.companyId) return;
    try {
      await controller.updateStatus(controller.companyId, record.id, status);
      toast.success(`${record.name} ${status === 'active' ? 'activated' : 'deactivated'}.`);
    } catch (error) {
      toast.error(getErrorMessage(
        error,
        `Could not ${status === 'active' ? 'activate' : 'deactivate'} the contact capture.`,
      ));
    }
  };

  const changeStatus = (record: ContactCapture) => {
    if (record.status !== 'active') {
      void runStatusChange(record, 'active');
      return;
    }
    setConfirm({
      title: 'Deactivate contact capture?',
      content: `${record.name} will stop collecting new contacts until you activate it again.`,
      confirmLabel: 'Deactivate',
      action: () => runStatusChange(record, 'inactive'),
    });
  };

  const duplicate = async (record: ContactCapture) => {
    if (!controller.companyId) return;
    try {
      await controller.duplicate(controller.companyId, record.id);
      toast.success(`${record.name} duplicated as a draft.`);
      await controller.reload();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not duplicate the contact capture.'));
    }
  };

  const preview = (record: ContactCapture) => {
    try {
      testContactCaptureRecord(record);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not load the contact capture preview.'));
    }
  };

  return {
    changeStatus,
    confirm,
    duplicate,
    preview,
    setConfirm,
  };
};

